import { cn } from '@client/utils/styling/cn';
import { Loader2 } from 'lucide-react';
import { cloneElement, forwardRef, isValidElement } from 'react';
import { LinkTooltip } from './LinkTooltip';
import type { InlineButtonProps } from './types';

/** The icon size beside text of the default 14px. */
const ICON_SIZE = 14;

/**
 * A text action that sits inside a line of text: "Change" beside a product, "View answers" beside a
 * status, "Use client's answer" under a value. It takes the line's own font, weight and height, with
 * no padding and no minimum height, so the line stays as tall as its text. `Button type="link"`
 * keeps the button sizes' 32px minimum height and stretches the row it sits in.
 *
 * For navigation inside text, use `InlineLink`; for a standalone action, `Button`.
 *
 * @example
 * <span>Fleet motor <Button.Inline onClick={openChooser}>Change</Button.Inline></span>
 *
 * @example
 * // Disabled by state: the reason shows on hover or keyboard focus
 * <Button.Inline disabled disabledReason="Payments are booked against this product.">Change</Button.Inline>
 */
export const ButtonInline = forwardRef<HTMLButtonElement, InlineButtonProps>(
    (
        {
            icon,
            iconPosition = 'start',
            tone = 'link',
            loading = false,
            danger,
            disabled,
            disabledReason,
            htmlType = 'button',
            className,
            children,
            ...props
        },
        ref,
    ) => {
        const inactive = disabled || loading;
        const iconNode = loading ? (
            <Loader2 className="animate-spin" size={ICON_SIZE} aria-hidden />
        ) : isValidElement(icon) ? (
            cloneElement(icon as React.ReactElement<{ size?: number; 'aria-hidden'?: boolean }>, {
                size: ICON_SIZE,
                'aria-hidden': true,
            })
        ) : (
            icon
        );

        return (
            <LinkTooltip disabled={disabled} disabledReason={disabledReason}>
                <button
                    ref={ref}
                    type={htmlType}
                    disabled={inactive}
                    aria-busy={loading || undefined}
                    className={cn(
                        'btn-inline transition-colors',
                        'focus-visible:outline-2 focus-visible:outline-accent-500 focus-visible:outline-offset-2',
                        inactive
                            ? 'cursor-not-allowed text-subtle-foreground'
                            : danger
                              ? 'text-danger-foreground hover:underline'
                              : tone === 'neutral'
                                ? 'text-foreground decoration-separator hover:underline'
                                : 'text-link decoration-link-hover hover:text-link-hover hover:underline active:text-link-active',
                        // A disabled button takes no pointer events, so the reason's wrapper gets the hover.
                        disabled && disabledReason ? 'pointer-events-none' : undefined,
                        className,
                    )}
                    {...props}
                >
                    {iconPosition === 'start' && iconNode}
                    {children}
                    {iconPosition === 'end' && iconNode}
                </button>
            </LinkTooltip>
        );
    },
);

ButtonInline.displayName = 'ButtonInline';
